/** Reading an issue's evidence, which has no fixed shape.
 *
 * The service stores `issues.detail` as a free-form JSON document, and that
 * is the right decision: what a `gate_failed` needs to say (crossings,
 * coverage, the gap that appeared) and what a `source_conflict` needs to say
 * (two account ids and a source id) have nothing in common, and a schema wide
 * enough for both would be a schema for neither. The cost lands here — the
 * screen has to render an object it has not been told the shape of.
 *
 * So this module reads defensively and prints plainly. Every scalar becomes a
 * labelled row; a key written `finish_crossings` is shown as words, because
 * the underscore is the job's spelling and not the admin's. Nothing is
 * invented: a value the job did not send produces no row rather than a row
 * saying nothing.
 *
 * Two kinds of entry are held back from the grid. The racing lines a
 * `layout_mismatch` carries are geometry, drawn side by side further down the
 * card, and dumping their coordinates as a string would bury the four facts
 * worth reading. The note an admin typed while resolving is their own words
 * and belongs with the resolution, not among the job's measurements.
 */

import { count, DASH } from '@/lib/format';
import type { Issue, IssueDetailRow } from '@/lib/types';

/** A point in drawing space: world x, and world z negated, as
 *  `TrackOutline` uses it. */
export type LinePoint = [number, number];

/** Written by `resolveIssue` into the detail document; shown with the
 *  resolution rather than with the evidence. */
const RESOLUTION_NOTE = 'resolution_note';

/** Where a `layout_mismatch` may have put the two lines. The job that raises
 *  these lives in another repository, so both the nested spelling and the
 *  flat one are accepted rather than one being declared correct. */
const CONTRIBUTOR_LINE_KEYS = ['contributor_line', 'contributor_racing_line', 'line'] as const;
const SIGNATURE_LINE_KEYS = ['signature_line', 'signatures_line', 'matched_line'] as const;
const CONTRIBUTOR_LABEL_KEYS = [
  'contributor_official_id',
  'contributor_layout',
  'claimed_official_id',
  'official_id',
] as const;
const SIGNATURE_LABEL_KEYS = [
  'signature_official_id',
  'signature_layout',
  'identified_official_id',
  'signature_match',
] as const;

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/** True for a value that is structure rather than a fact: a list of points, a
 *  nested document. Those are drawn, or left out, but never stringified into
 *  a cell. */
function isStructure(value: unknown): boolean {
  if (isRecord(value)) return true;
  if (!Array.isArray(value)) return false;
  return value.some((entry) => Array.isArray(entry) || isRecord(entry));
}

/** `finish_crossings` → `finish crossings`. */
function label(key: string): string {
  return key.replace(/[_.]+/g, ' ').trim();
}

function scalar(value: unknown): string {
  if (value === null || value === undefined || value === '') return DASH;
  if (typeof value === 'number') return count(value, Number.isInteger(value) ? 0 : 3);
  if (typeof value === 'boolean') return value ? 'yes' : 'no';
  return String(value);
}

/**
 * The evidence as rows, in the order the job wrote them.
 *
 * Empty for an issue whose detail is empty, which is a real case: a job may
 * raise something whose summary says all there is to say.
 */
export function detailRows(detail: Record<string, unknown>): IssueDetailRow[] {
  const rows: IssueDetailRow[] = [];

  for (const [key, value] of Object.entries(detail)) {
    if (key === RESOLUTION_NOTE) continue;
    if (isStructure(value)) continue;

    if (Array.isArray(value)) {
      // A flat list — two account ids, three attempt times — reads better
      // joined than bracketed.
      rows.push({ key: label(key), value: value.map(scalar).join(' · ') || DASH });
      continue;
    }
    rows.push({ key: label(key), value: scalar(value) });
  }

  return rows;
}

/** What the admin typed when they closed this, if they typed anything. */
export function resolutionNote(detail: Record<string, unknown>): string {
  const note = detail[RESOLUTION_NOTE];
  return typeof note === 'string' ? note : '';
}

function pick(detail: Record<string, unknown>, keys: readonly string[]): unknown {
  for (const key of keys) {
    const value = detail[key];
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return undefined;
}

/** `[x, z]`, `[x, z, y]` or `{ x, z }` — all three reach the same drawing. */
function toPoint(entry: unknown): LinePoint | null {
  if (Array.isArray(entry)) {
    const x = entry[0];
    const z = entry[1];
    if (typeof x === 'number' && typeof z === 'number' && Number.isFinite(x) && Number.isFinite(z)) {
      return [x, -z];
    }
    return null;
  }
  if (isRecord(entry)) {
    const x = entry['x'];
    const z = entry['z'];
    if (typeof x === 'number' && typeof z === 'number' && Number.isFinite(x) && Number.isFinite(z)) {
      return [x, -z];
    }
  }
  return null;
}

/** A racing line, wherever in the value it is hiding: a bare list of points,
 *  or a document with one under `line` or `points`. */
function toLine(value: unknown): LinePoint[] | null {
  const list = Array.isArray(value)
    ? value
    : isRecord(value)
      ? ((value['line'] ?? value['points']) as unknown)
      : null;
  if (!Array.isArray(list)) return null;

  const points: LinePoint[] = [];
  for (const entry of list) {
    const point = toPoint(entry);
    if (point) points.push(point);
  }
  return points.length >= 2 ? points : null;
}

export interface ComparedLine {
  /** The layout this line says the survey is of. */
  label: string;
  /** Null when the job sent the two names but not the geometry. */
  points: LinePoint[] | null;
}

export interface LineComparison {
  contributor: ComparedLine;
  signature: ComparedLine;
}

/**
 * The two readings of one survey, for the side-by-side on a
 * `layout_mismatch`: the layout the contributor confirmed against the one
 * `signatures.json` identified from the compiled line.
 *
 * Returns null for any other issue type, and for a `layout_mismatch` whose
 * detail names neither layout — there is nothing to compare, and two empty
 * panels would only say the job sent less than the screen hoped for. When the
 * names are there but the geometry is not, the panels still appear: which two
 * layouts are in dispute is the point, and the stylised outline says "a
 * circuit, drawn in the direction this reading walks it".
 */
export function lineComparison(issue: Pick<Issue, 'type' | 'detail'>): LineComparison | null {
  if (issue.type !== 'layout_mismatch') return null;

  const detail = issue.detail;
  const nested = isRecord(detail['comparison']) ? detail['comparison'] : null;
  const source = nested ?? detail;

  const contributorLabel = pick(source, CONTRIBUTOR_LABEL_KEYS) ?? pick(detail, CONTRIBUTOR_LABEL_KEYS);
  const signatureLabel = pick(source, SIGNATURE_LABEL_KEYS) ?? pick(detail, SIGNATURE_LABEL_KEYS);
  if (contributorLabel === undefined && signatureLabel === undefined) return null;

  const contributorValue =
    (isRecord(source['contributor']) ? source['contributor'] : undefined) ??
    pick(source, CONTRIBUTOR_LINE_KEYS);
  const signatureValue =
    (isRecord(source['signature']) ? source['signature'] : undefined) ??
    pick(source, SIGNATURE_LINE_KEYS);

  return {
    contributor: {
      label: scalar(contributorLabel ?? 'the contributor'),
      points: toLine(contributorValue),
    },
    signature: {
      label: scalar(signatureLabel ?? 'signatures.json'),
      points: toLine(signatureValue),
    },
  };
}
